<template>
    <validation-observer ref="registration_form" v-slot="{ meta, handleSubmit, errors, setFieldValue }">
        <modal
            title="Registration Data"
            :id="registrationStatusModalId"
            ok-title="Save"
            cancel-title="Close"
            :ok-disabled="!meta.valid"
            @ok.prevent="handleSubmit(_, submitForm)"
            @hidden="$emit('hidden')"
            :scrollable="registrationData.numberRequirements.length > 1 ? true : false"
        >
            <busy-modal :show="loading"></busy-modal>
            <b-form class="registration-form">
                <validation-provider name="category" rules="required">
                    <form-select
                        floatingLabel="Regulatory Category"
                        :options="regulatoryCategoryOptions"
                        v-model="registrationData.regulatoryCategory"
                        @change="onRegulatoryCategoryChange"
                        :error="errors.category"
                        class="mb-0"
                    />
                </validation-provider>
                <validation-provider v-if="regulatoryTypeOptions.length > 1" name="type" rules="required">
                    <form-select
                        floatingLabel="Regulatory Type"
                        :options="regulatoryTypeOptions"
                        v-model="registrationData.regulatoryType"
                        :error="errors[0]"
                        class="mb-0 mt-2"
                        @change="(val) => setFieldValue('type', val)"
                    />
                </validation-provider>
                <validation-provider v-if="regulatorySubTypeOptions.length > 1" name="sub_type" rules="required">
                    <form-select
                        floatingLabel="Regulatory Sub Type"
                        :options="regulatorySubTypeOptions"
                        v-model="registrationData.regulatorySubType"
                        :error="errors.sub_type"
                        class="mb-0 mt-2"
                        @change="(val) => setFieldValue('sub_type', val)"
                    />
                </validation-provider>
                <div
                    class="registration-form__requirement mt-3"
                    v-for="requirement in registrationData.numberRequirements"
                    :key="requirement.id"
                >
                    <span>{{ requirement.label }}</span>
                    <validation-provider
                        v-if="requirement.field_type === 'text'"
                        :name="`number` + requirement.id"
                        :rules="{
                            required: !requirement.is_optional,
                            regex: requirement.regex,
                            maxMessageLen: [255, 'Registration Number'],
                        }"
                    >
                        <form-input
                            name="Number"
                            v-model="requirement.record.number"
                            :error="errors['number' + requirement.id]"
                            class="mt-1 mb-0"
                            @change="(val) => setFieldValue('number' + requirement.id, val)"
                        />
                        <small v-if="requirement.format" class="text-secondary">
                            Example: {{ requirement.format }}
                        </small>
                    </validation-provider>
                    <validation-provider
                        v-else-if="requirement.field_type === 'radio'"
                        :name="`number` + requirement.id"
                        :rules="{
                            required: !requirement.is_optional,
                        }"
                    >
                        <b-form-group
                            :state="errors['number' + requirement.id] ? false : null"
                            :invalid-feedback="errors['number' + requirement.id]"
                        >
                            <b-form-radio-group
                                :name="`radio-group-${requirement.id}`"
                                v-model="requirement.record.number"
                                :options="requirement.options"
                                :state="errors['number' + requirement.id] ? false : null"
                                class="mt-1 mb-0"
                                @change="(val) => setFieldValue('number' + requirement.id, val)"
                            />
                        </b-form-group>
                    </validation-provider>
                    <validation-provider
                        v-else-if="requirement.field_type === 'date'"
                        :name="`number` + requirement.id"
                        :rules="{
                            required: !requirement.is_optional,
                        }"
                    >
                        <form-datepicker
                            v-model="requirement.record.number"
                            reset-button
                            :hide-header="true"
                            :error="errors['number' + requirement.id]"
                            class="mt-2 mb-0"
                            @input="(val) => setFieldValue('number' + requirement.id, val)"
                        />
                    </validation-provider>
                    <validation-provider
                        v-if="requirement.is_expiration_date_required"
                        :name="'expiry' + requirement.id"
                        rules="required"
                    >
                        <form-datepicker
                            floating-label="Expiration Date"
                            v-model="requirement.record.expiration_date"
                            reset-button
                            :hide-header="true"
                            :error="errors['expiry' + requirement.id]"
                            class="mt-2 mb-0"
                            @input="(val) => setFieldValue('expiry' + requirement.id, val)"
                        />
                    </validation-provider>
                    <hr />
                </div>
            </b-form>
            <modal title="Warning" :id="warningModal" ok-only header-bg-variant="warning">
                <span class="warning">{{ warningDescription }}</span>
            </modal>
        </modal>
    </validation-observer>
</template>

<script>
import API from '../../../api';
import FormDatepicker from '../../generic/form-datepicker/form-datepicker.vue';
import FormInput from '../../generic/form-input/form-input.vue';
import FormSelect from '../../generic/form-select/form-select.vue';
import BusyModal from '../../generic/busy-modal/busy-modal.vue';
import Modal from '../../generic/modal/modal.vue';
import { formatResponseError } from '../../../utils/api';
import { PARTNER_VRBO_SHARE_FEED_UPDATE_REGISTRATION_ENDPOINT } from '../../../endpoints';
import { defineRule as extend } from 'vee-validate';
import { regex } from '@vee-validate/rules';

extend('regex', regex);

export default {
    components: {
        BusyModal,
        Modal,
        FormDatepicker,
        FormInput,
        FormSelect,
    },
    props: {
        registrationStatusModalId: String,
        feedShare: Object,
        feedShareNode: Object,
    },
    data() {
        return {
            loading: false,
            registrationData: {
                regulatoryCategory: '',
                regulatoryType: '',
                regulatorySubType: '',
                numberRequirements: [],
            },
            warningCode: '',
            warningModal: 'warningModal',
            defaultOption: {
                text: '-- Please select an option --',
                value: '',
                disabled: true,
            },
        };
    },
    mounted() {
        this.generateRegistrationData();
    },
    computed: {
        registrationNumberRecords() {
            return this.registrationData.numberRequirements.map((requirement) => {
                return {
                    type: requirement.type,
                    number: requirement.record.number,
                    expiration_date:
                        requirement.is_expiration_date_required && !!requirement.record.expiration_date
                            ? requirement.record.expiration_date
                            : null,
                };
            });
        },
        currentRequirement() {
            return this.feedShare?.registration_data?.requirements.find((requirement) => {
                return requirement.category === this.registrationData.regulatoryCategory;
            });
        },
        regulatoryCategoryOptions() {
            const regulatoryCategoryOptions = [this.defaultOption];
            if (!!this.feedShare?.registration_data?.requirements) {
                regulatoryCategoryOptions.push(
                    ...this.feedShare.registration_data.requirements.map((requirement) => {
                        return {
                            text: requirement.label,
                            value: requirement.category,
                        };
                    })
                );
            }
            return regulatoryCategoryOptions;
        },
        regulatoryTypeOptions() {
            const regulatoryTypeOptions = [this.defaultOption];
            if (!!this.currentRequirement) {
                regulatoryTypeOptions.push(
                    ...this.currentRequirement.property_types
                        .filter((obj, index, array) => {
                            return index === array.findIndex((regulatoryType) => regulatoryType.type === obj.type);
                        })
                        .map((regulatoryType) => {
                            return {
                                text: regulatoryType.type_label,
                                value: regulatoryType.type,
                            };
                        })
                );
            }
            return regulatoryTypeOptions;
        },
        regulatorySubTypeOptions() {
            const regulatorySubTypeOptions = [this.defaultOption];
            if (!!this.currentRequirement) {
                regulatorySubTypeOptions.push(
                    ...this.currentRequirement.property_types
                        .filter((regulatoryType) => {
                            return (
                                regulatoryType.type === this.registrationData.regulatoryType &&
                                !!regulatoryType.sub_type
                            );
                        })
                        .map((regulatoryType) => {
                            return {
                                text: regulatoryType.sub_type_label,
                                value: regulatoryType.sub_type,
                            };
                        })
                );
            }
            return regulatorySubTypeOptions;
        },
        warningDescription() {
            if (!!this.warningCode) {
                const warningCodes = this.warningCode.split(', ');
                let warningDescriptions = [];
                warningCodes.forEach((warningCode) => {
                    if (warningCode === 'INVALID_LICENSE_NUMBERS') {
                        warningDescriptions.push(
                            'An invalid registration number was specified. Please, try ' +
                                'to resync the property, to fetch valid formats of registration numbers.'
                        );
                    } else if (warningCode === 'REGULATORY_CATEGORY_NOT_CONFIGURED_FOR_DISTRICT') {
                        warningDescriptions.push(
                            'The specified regulatory category is not supported by the ' +
                                "property's district. Please, try to resync the property and try again."
                        );
                    } else if (warningCode === 'REGISTRATION_NUMBER_TYPE_MISSING') {
                        warningDescriptions.push(
                            'Registration number type was not specified. ' +
                                'Please, try to resync the property and try again.'
                        );
                    } else if (warningCode === 'REGISTRATION_NUMBER_TYPE_NOT_APPLICABLE') {
                        warningDescriptions.push(
                            'The specified registration number type is not applicable to the property type. ' +
                                'Please, try to resync the property and try again.'
                        );
                    } else if (warningCode === 'MISSING_ADDITIONAL_DATA') {
                        warningDescriptions.push(
                            'Additional registration data is required. ' + 'It is not possible to set this data yet.'
                        );
                    } else if (warningCode === 'MISSING_PROPERTY_ADDITIONAL_DATA') {
                        warningDescriptions.push(
                            'Additional property registration data is required. ' +
                                'It is not possible to set this data yet.'
                        );
                    } else if (warningCode === 'INVALID_REGULATORY_CATEGORY') {
                        warningDescriptions.push(
                            'An invalid regulatory category was specified. ' +
                                'Please, try to resync the property and try again.'
                        );
                    } else if (warningCode === 'DIFFERENT_REGULATORY_CATEGORIES') {
                        warningDescriptions.push(
                            'The same regulatory category must be specified for all bookable units ' +
                                'of the property. Please, try to resync the property and try again.'
                        );
                    } else if (warningCode === 'MISSING_REGISTRATION') {
                        warningDescriptions.push(
                            'Regulatory information is missing for the property or its bookable unit. ' +
                                'Please, try to resync the property and try again.'
                        );
                    } else {
                        warningDescriptions.push(`Unknown warning code: ${warningCode}.`);
                    }
                });
                return warningDescriptions.join('\n');
            }
            return '';
        },
    },
    methods: {
        async submitForm() {
            this.loading = true;
            try {
                const { data } = await API.post(
                    PARTNER_VRBO_SHARE_FEED_UPDATE_REGISTRATION_ENDPOINT(this.feedShare.id),
                    {
                        category: this.registrationData.regulatoryCategory,
                        type: this.registrationData.regulatoryType,
                        sub_type: this.registrationData.regulatorySubType,
                        records: this.registrationNumberRecords,
                    }
                );
                this.feedShareNode.setData(data);
                if (!!data.registration_data) {
                    this.warningCode = data.registration_data.warning_code;
                } else {
                    this.warningCode = '';
                }
                this.successToast({ text: 'Registration data were successfully saved.' });
                if (!!this.warningDescription) {
                    this.$bvModal.show(this.warningModal);
                } else {
                    this.$bvModal.hide(this.registrationStatusModalId);
                }
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not save registration data.' });
            } finally {
                this.loading = false;
            }
        },
        generateNumberRequirements() {
            if (!!this.currentRequirement) {
                const currentRequirement = JSON.parse(JSON.stringify(this.currentRequirement));
                this.registrationData.numberRequirements = currentRequirement.number_requirements.map(
                    (numberRequirement) => {
                        const requirement = {
                            ...numberRequirement,
                            regex: !!numberRequirement.regex ? new RegExp(numberRequirement.regex) : false,
                        };
                        if (!requirement.record) {
                            return {
                                ...requirement,
                                record: {
                                    number: '',
                                    expiration_date: null,
                                },
                            };
                        }
                        if (numberRequirement.is_expiration_date_required) {
                            this.$refs.registration_form.setFieldValue(
                                `expiry${requirement.id}`,
                                requirement.record.expiration_date
                            );
                        }
                        this.$refs.registration_form.setFieldValue(
                            `number${requirement.id}`,
                            requirement.record.number
                        );
                        return requirement;
                    }
                );
            } else {
                this.registrationData.numberRequirements = [];
            }
        },
        generateRegistrationData() {
            if (!!this.feedShare?.registration_data?.detail) {
                this.registrationData.regulatoryCategory = this.feedShare.registration_data.detail.category;
                this.registrationData.regulatoryType = this.feedShare.registration_data.detail.type;
                this.registrationData.regulatorySubType = this.feedShare.registration_data.detail.sub_type;
            } else {
                this.registrationData.regulatoryCategory = '';
                this.registrationData.regulatoryType = '';
                this.registrationData.regulatorySubType = '';
            }
            this.generateNumberRequirements();
            this.$refs.registration_form.setFieldValue('category', this.registrationData.regulatoryCategory);
            this.$refs.registration_form.setFieldValue('type', this.registrationData.regulatoryType);
            this.$refs.registration_form.setFieldValue('sub_type', this.registrationData.regulatorySubType);
        },
        onRegulatoryCategoryChange() {
            this.generateNumberRequirements();
            if (
                !!this.feedShare?.registration_data?.detail &&
                !!this.currentRequirement &&
                this.currentRequirement.category === this.feedShare.registration_data.detail.category
            ) {
                this.registrationData.regulatoryType = this.feedShare.registration_data.detail.type;
                this.registrationData.regulatorySubType = this.feedShare.registration_data.detail.sub_type;
            } else {
                this.registrationData.regulatoryType = '';
                this.registrationData.regulatorySubType = '';
            }
            this.$refs.registration_form.setFieldValue('category', this.registrationData.regulatoryCategory);
        },
    },
    watch: {
        feedShare() {
            this.generateRegistrationData();
            this.warningCode = '';
        },
    },
};
</script>

<style lang="scss" scoped>
.registration-form {
    &__requirement {
        > hr {
            margin: 1.25rem 0;
        }
        &:last-child > hr {
            display: none;
        }
    }
}
.warning {
    white-space: pre-line;
}
</style>
